// Invitee inbox hooks — the WRAPPER layer (lint-exempt for raw TanStack
// Query). PERSON-scoped (like useProfile): the keys live under ['me', 'invites'] so they survive a band
// switch. Accepting an invite mints a seat/link, so it invalidates both the inbox and ['me'] (the new seat
// surfaces in the account chip / shell selection). Accept surfaces the server error verbatim (404 expired /
// 409 already-accepted) and NEVER reports success on an unpersisted write.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";

type MyInvitesRead = components["schemas"]["MyInvitesRead"];
type MyInvite = components["schemas"]["MyInvite"];
type AcceptInviteResult = components["schemas"]["AcceptInviteResult"];
type InvitePreview = components["schemas"]["InvitePreview"];
type InviteLink = components["schemas"]["InviteLink"];

export type { MyInvitesRead, MyInvite, AcceptInviteResult, InvitePreview, InviteLink };

/** `GET /me/invites` — the caller's pending invites (band seats, org links, org seats, partner). `enabled`
 *  lets a surface that may render signed-out (the root-level host) skip the read. */
export function useMyInvites({ enabled = true }: { enabled?: boolean } = {}) {
  const { api } = useApp();
  return useQuery({
    enabled,
    queryKey: ["me", "invites"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/invites");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 30_000,
  });
}

/** `POST /me/invites/{inviteId}/accept` → AcceptInviteResult. Invalidates the inbox + /me (a new seat/link). */
export function useAcceptInvite() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data, error, response } = await api.POST("/api/v1/me/invites/{inviteId}/accept", {
        params: { path: { inviteId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["me", "invites"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
      // The seat_invite bell row this answered is cleared server-side (ART-239).
      void qc.invalidateQueries({ queryKey: ["me", "notifications"] });
    },
  });
}

/** `POST /me/invites/{inviteId}/decline` → 204. Runs the decline gate (an org_link decline pings the
 *  issuer); invalidates the inbox. Surfaces the server error verbatim (404 not-yours / 409 already-used). */
export function useDeclineInvite() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error, response } = await api.POST("/api/v1/me/invites/{inviteId}/decline", {
        params: { path: { inviteId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["me", "invites"] });
      void qc.invalidateQueries({ queryKey: ["me", "notifications"] });
    },
  });
}

/**
 * `POST /public/invites/preview` — what the `/join` page may show the holder of an invite link before they have
 * an account (ART-239). PUBLIC: no bearer needed. `null` for an unknown or malformed link (the server's 404),
 * which the page renders as "no longer valid" — the same sentence as a used or expired one, by design.
 */
export function useInvitePreview(token: string) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["public", "invite-preview", token],
    queryFn: async (): Promise<InvitePreview | null> => {
      const { data, error, response } = await api.POST("/api/v1/public/invites/preview", { body: { token } });
      if (response.status === 404) return null;
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    enabled: token.length > 0,
    retry: false,
    staleTime: 0,
  });
}

/** The /join link for an invite we only ever hold the HASH of — "Copy link" re-issues it (the old link dies). */
export type ReissueTarget =
  | { kind: "band_seat"; bandId: string; inviteId: string }
  | { kind: "org_seat"; orgId: string; inviteId: string }
  | { kind: "partner"; bandId: string; inviteId: string };

/**
 * "Copy link" on a pending invite (ART-239): mints a fresh `/join` link with a fresh expiry and kills the old one.
 * Returns the link for the caller to copy or share. Invalidates the issuing surface's pending list, whose expiry
 * just moved.
 */
export function useReissueInviteLink() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation<InviteLink, Error, ReissueTarget>({
    mutationFn: async (t) => {
      const res =
        t.kind === "org_seat"
          ? await api.POST("/api/v1/orgs/{orgId}/team/invites/{inviteId}/link", {
              params: { path: { orgId: t.orgId, inviteId: t.inviteId } },
            })
          : t.kind === "partner"
            ? await api.POST("/api/v1/bands/{bandId}/partner-links/invites/{inviteId}/link", {
                params: { path: { bandId: t.bandId, inviteId: t.inviteId } },
              })
            : await api.POST("/api/v1/bands/{bandId}/team/invites/{inviteId}/link", {
                params: { path: { bandId: t.bandId, inviteId: t.inviteId } },
              });
      if (!res.response.ok || !res.data) throw new ApiError(res.response.status, res.error);
      return res.data;
    },
    onSuccess: (_link, t) => {
      // Only the pending list whose expiry just moved — never the whole tenant namespace.
      const key =
        t.kind === "org_seat"
          ? ["org", t.orgId, "team", "invites"]
          : t.kind === "partner"
            ? ["band", t.bandId, "partnerLinks"]
            : ["band", t.bandId, "team", "invites"];
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}
